// pages/BarcodeTranslationView.jsx - Public page for viewing translation details via barcode
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// Services
import documentService from '../services/documentService';
import useApiClient from '../services/api';

// Components
import Spinner from '../components/Spinner';
import DocumentViewer from '../components/DocumentViewer';

const BarcodeTranslationView = () => {
  const { translationId } = useParams();
  const [searchParams] = useSearchParams();
  const apiClient = useApiClient();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [translation, setTranslation] = useState(null);
  const [htmlFiles, setHtmlFiles] = useState([]);
  const [htmlContent, setHtmlContent] = useState('');

  // Initialize selectedPage from URL query param or default to 1
  const [selectedPage, setSelectedPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });

  // Fetch translation data
  useEffect(() => {
    const fetchTranslationData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get translation by ID (public endpoint)
        const response = await apiClient.get(`/translations/public/${translationId}`);

        if (response.data.success) {
          const translationData = response.data.translation;
          setTranslation(translationData);

          // Fetch translated HTML content for the document
          if (translationData?.documentId) {
            await fetchTranslatedHtmls(translationData.documentId);
          } else {
            setError('Document ID not found in translation');
          }
        } else {
          setError('Failed to load translation');
        }
      } catch (error) {
        console.error('Error fetching translation data:', error);

        if (error.response?.status === 404) {
          setError('Translation not found. The link may be invalid.');
        } else if (error.response?.status === 403) {
          setError('This translation is not publicly accessible.');
        } else {
          setError('Failed to load translation. Please try again or contact support.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (translationId) {
      fetchTranslationData();
    } else {
      setError('No translation ID provided');
      setLoading(false);
    }
  }, [translationId]);

  // Fetch translated HTML files for a document
  const fetchTranslatedHtmls = async (documentId) => {
    try {
      const response = await documentService(apiClient).getTranslatedHTMLs(documentId);

      if (response.success) {
        // Set HTML files
        if (response.htmlFiles && response.htmlFiles.length > 0) {
          setHtmlFiles(response.htmlFiles);

          // Load the first page or the page from URL
          const pageParam = searchParams.get('page');
          const pageToLoad = pageParam ? parseInt(pageParam, 10) : 1;
          const validPage = Math.min(Math.max(pageToLoad, 1), response.htmlFiles.length);

          if (response.htmlFiles[validPage - 1]) {
            setSelectedPage(validPage);
            setHtmlContent(response.htmlFiles[validPage - 1].content);
          }
        } else {
          console.warn('No translated HTML files found');
        }
      } else {
        console.warn('Failed to fetch translated content');
      }
    } catch (error) {
      console.error('Error fetching translated HTMLs:', error);
      // Don't show error - we'll just show what we have
    }
  };

  // Handle page selection change
  const handlePageChange = (pageNumber) => {
    const pageIndex = pageNumber - 1;
    if (htmlFiles[pageIndex]) {
      setSelectedPage(pageNumber);
      setHtmlContent(htmlFiles[pageIndex].content);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // TinyMCE Preview Component - renders content with TinyMCE preview styles
  const TinyMCEPreview = ({ htmlContent }) => {
    const iframeRef = React.useRef(null);

    React.useEffect(() => {
      if (!iframeRef.current || !htmlContent) return;

      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

      // Extract custom styles from content
      const extractStylesFromHTML = (htmlContent) => {
        if (!htmlContent) return '';
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        const styleTags = tempDiv.querySelectorAll('style');
        let extractedStyles = '';
        styleTags.forEach(styleTag => {
          extractedStyles += styleTag.textContent + '\n';
        });
        return extractedStyles;
      };

      const customStyles = extractStylesFromHTML(htmlContent);
      const contentWithoutStyles = htmlContent.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

      // Generate preview HTML with TinyMCE styles
      const previewHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #333;
              background: white;
              margin: 20px;
              padding: 0;
            }
            ${customStyles}
            table {
              border-collapse: collapse;
              width: 100%;
              margin: 10px 0;
              border: 1px solid #ccc;
            }
            table th, table td {
              border: 1px solid #ccc;
              padding: 8px;
              text-align: left;
              vertical-align: top;
            }
            table th {
              background: #f5f5f5;
              font-weight: bold;
            }
            h1, h2, h3, h4, h5, h6 {
              margin: 15px 0 10px 0;
              font-weight: bold;
              line-height: 1.3;
            }
            p { margin: 8px 0; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          ${contentWithoutStyles}
        </body>
        </html>
      `;

      iframeDoc.open();
      iframeDoc.write(previewHTML);
      iframeDoc.close();
    }, [htmlContent]);

    return (
      <iframe
        ref={iframeRef}
        className="w-full h-full border-none"
        title="Translation Preview"
        sandbox="allow-same-origin"
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Spinner />
          <p className="mt-4 text-gray-600">Loading translation details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Unable to Load Translation</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  // Get original document preview URL
  const originalDocumentUrl = translation?.documentId
    ? documentService(apiClient).getOriginalDocumentPreviewUrl(translation.documentId)
    : null;

  return (
    <div className="barcode-translation-view min-h-screen bg-gray-50 flex flex-col">
      {/* Header with Logo and Authenticity Badge */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          {/* Logo and Title Row */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Logo - Using text for now, replace with actual logo image if needed */}
              <div className="text-2xl sm:text-3xl font-bold text-blue-600">
                Juree
              </div>
              <div className="h-6 sm:h-8 w-px bg-gray-300"></div>
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
                Translation Verification
              </h1>
            </div>

            {/* Authenticity Badge - Only show if completed */}
            {translation?.status === 'COMPLETED' && (
              <div className="flex items-center gap-2 sm:gap-3 bg-green-50 px-3 sm:px-6 py-2 sm:py-3 rounded-lg border-2 border-green-500 shadow-sm">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <div className="text-xs sm:text-sm font-bold text-green-800">AUTHENTICATED</div>
                  <div className="text-xs text-green-700">Certified Translation</div>
                </div>
              </div>
            )}
          </div>

          {/* Document Info */}
          <div className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 break-words">
                  {translation?.originalFileName || 'N/A'}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 break-all">
                  Translation ID: <span className="font-mono text-gray-800">{translationId}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Translation Metadata */}
          <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-500 uppercase mb-2">Translator</h3>
              <p className="text-sm sm:text-lg font-medium text-gray-900 break-words">
                {translation?.translator?.name || 'Not assigned'}
              </p>
              {translation?.translator?.email && (
                <p className="text-xs sm:text-sm text-gray-600 break-words">{translation.translator.email}</p>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-500 uppercase mb-2">Completion Date</h3>
              <p className="text-sm sm:text-lg font-medium text-gray-900">
                {translation?.completedAt ? formatDate(translation.completedAt) : 'Not completed'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-500 uppercase mb-2">Languages</h3>
              <p className="text-sm sm:text-lg font-medium text-gray-900">
                {translation?.sourceLanguage || 'N/A'} → {translation?.targetLanguage || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Document Viewer Section - Wider container */}
      <div className="flex-1 w-full px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-full">
          {/* Toolbar */}
          <div className="bg-gray-800 text-white px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
              <h2 className="text-base sm:text-lg font-semibold">Translated Document</h2>

              {/* Page Navigation */}
              {htmlFiles.length > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(selectedPage - 1)}
                    disabled={selectedPage <= 1}
                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ◀
                  </button>
                  <span className="text-xs sm:text-sm font-medium text-gray-300 whitespace-nowrap">
                    Page {selectedPage} of {htmlFiles.length}
                  </span>
                  <button
                    onClick={() => handlePageChange(selectedPage + 1)}
                    disabled={selectedPage >= htmlFiles.length}
                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ▶
                  </button>
                </div>
              )}
            </div>

            <div className="text-xs sm:text-sm text-gray-400">
              {translation?.documentType || 'Document'}
            </div>
          </div>

          {/* Document Preview - Increased height for desktop */}
          <div className="bg-white" style={{ height: '500px', minHeight: '400px' }}>
            {htmlContent ? (
              <TinyMCEPreview htmlContent={htmlContent} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-4xl sm:text-6xl mb-4">📄</div>
                  <p className="text-sm sm:text-base text-gray-500">No translation content available</p>
                </div>
              </div>
            )}
          </div>

          {/* Original Document Section (Optional) */}
          {originalDocumentUrl && (
            <div className="border-t border-gray-200">
              <details className="group">
                <summary className="cursor-pointer px-3 sm:px-6 py-3 sm:py-4 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between">
                  <span className="text-sm sm:text-base font-semibold text-gray-700">View Original Document</span>
                  <span className="text-gray-500 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-3 sm:p-6 bg-white" style={{ height: '400px', minHeight: '300px' }}>
                  <DocumentViewer
                    documentUrl={originalDocumentUrl}
                    title={translation?.originalFileName || 'Original Document'}
                    documentType={translation?.documentType}
                    initialHeight="100%"
                    showPageControls={true}
                  />
                </div>
              </details>
            </div>
          )}
        </div>

        {/* Additional Information */}
        <div className="mt-4 sm:mt-6 bg-white rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Additional Information</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <dt className="text-xs sm:text-sm font-medium text-gray-500">Translation ID</dt>
              <dd className="mt-1 text-xs sm:text-sm text-gray-900 font-mono break-all">{translationId}</dd>
            </div>
            <div>
              <dt className="text-xs sm:text-sm font-medium text-gray-500">Created Date</dt>
              <dd className="mt-1 text-xs sm:text-sm text-gray-900">
                {translation?.createdAt ? formatDate(translation.createdAt) : 'N/A'}
              </dd>
            </div>
            {translation?.projectId && (
              <div>
                <dt className="text-xs sm:text-sm font-medium text-gray-500">Project ID</dt>
                <dd className="mt-1 text-xs sm:text-sm text-gray-900 font-mono break-all">{translation.projectId}</dd>
              </div>
            )}
            {translation?.documentType && (
              <div>
                <dt className="text-xs sm:text-sm font-medium text-gray-500">Document Type</dt>
                <dd className="mt-1 text-xs sm:text-sm text-gray-900">{translation.documentType}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Footer - Mobile Responsive */}
      <footer className="mt-auto bg-white border-t border-gray-200 py-4 sm:py-6">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 sm:gap-4">
            {/* Company Info */}
            <div className="text-center md:text-left">
              <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 justify-center md:justify-start mb-2">
                <div className="text-lg sm:text-xl font-bold text-blue-600">Juree</div>
                <span className="hidden sm:inline text-gray-400">|</span>
                <span className="text-xs sm:text-sm text-gray-600">Certified Translation Services</span>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                36 RUE SAINT DIDIER, 75116 PARIS, FRANCE
              </p>
            </div>

            {/* Contact & Copyright */}
            <div className="text-center md:text-right">
              <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 mb-1">
                <a href="tel:+33171736436" className="text-xs sm:text-sm text-gray-600 hover:text-blue-600 transition-colors">
                  +33 (1) 71 73 64 36
                </a>
                <span className="hidden sm:inline text-gray-400">|</span>
                <a href="mailto:contact@juri.fr" className="text-xs sm:text-sm text-gray-600 hover:text-blue-600 transition-colors">
                  contact@juri.fr
                </a>
              </div>
              <p className="text-xs text-gray-500">
                © {new Date().getFullYear()} Juree Inc. All rights reserved.
              </p>
            </div>
          </div>

          {/* Verification Note */}
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
            <p className="text-xs text-center text-gray-500 leading-relaxed px-2">
              This document has been verified and authenticated by Juree's translation system.
              For any questions regarding this translation, please contact us using the information above.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BarcodeTranslationView;
